import { useMemo, useState, type ChangeEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { doc, serverTimestamp, setDoc } from 'firebase/firestore';
import { ref as storageRef, uploadBytes, getDownloadURL } from 'firebase/storage';
import {
  emptyFamilyMember,
  displayName,
  FAMILY_RELATIONSHIPS,
  ADDRESS_KINDS,
  PHONE_KINDS,
  MARITAL_STATUSES,
  ID_DOC_KINDS,
  BLOOD_TYPES,
  type FamilyMember,
  type EmailAddress,
  type PhoneNumber,
  type Address,
  type EmergencyContact,
  type IdDocument,
  type Doctor,
  type Prescription,
  type InsurancePolicy,
  type AddressKind,
  type PhoneKind,
  type IdDocKind,
  type BloodType,
  type MaritalStatus,
  type FamilyRelationship,
} from '@sfp/shared/family-member';
import { db, storage } from '../../lib/firebase.js';
import { useAuth } from '../../lib/auth.js';

type WizardMember = FamilyMember & { draft?: boolean };

const STEPS = [
  { key: 'basics', label: 'Basics' },
  { key: 'contact', label: 'Contact' },
  { key: 'identity', label: 'Identity' },
  { key: 'medical', label: 'Medical' },
  { key: 'financial', label: 'Financial' },
  { key: 'estate', label: 'Estate' },
  { key: 'review', label: 'Review' },
] as const;

type StepKey = (typeof STEPS)[number]['key'];

const NET_WORTH_BANDS: Array<{ value: NonNullable<FamilyMember['financial']['netWorthBand']>; label: string }> = [
  { value: 'under_1m', label: 'Under $1M' },
  { value: '1m_5m', label: '$1M – $5M' },
  { value: '5m_25m', label: '$5M – $25M' },
  { value: '25m_100m', label: '$25M – $100M' },
  { value: 'over_100m', label: 'Over $100M' },
  { value: 'unknown', label: 'Unknown' },
];

const INSURANCE_KINDS: Array<InsurancePolicy['kind']> = [
  'health',
  'dental',
  'vision',
  'life',
  'disability',
  'auto',
  'home',
  'umbrella',
  'other',
];

const LABEL_CLS = 'text-[10px] uppercase tracking-wider text-stone-500';
const INPUT_CLS =
  'w-full rounded border border-stone-300 px-3 py-2 text-sm focus:border-stone-500 focus:outline-none';
const CARD_CLS = 'rounded-2xl border border-stone-200 bg-white shadow-sm p-6';
const PRIMARY_BTN =
  'inline-flex items-center justify-center rounded bg-[#BF5700] px-4 py-2 text-sm font-medium text-white hover:opacity-90 disabled:opacity-50';
const SECONDARY_BTN =
  'inline-flex items-center justify-center rounded border border-stone-300 bg-white px-4 py-2 text-sm font-medium text-stone-700 hover:bg-stone-50 disabled:opacity-50';
const DANGER_BTN =
  'inline-flex items-center justify-center rounded border border-stone-300 bg-white px-2 py-1 text-xs text-[#9C4A3A] hover:bg-stone-50';

function sanitizeName(name: string): string {
  return name.replace(/[^a-zA-Z0-9._-]+/g, '_');
}

function uid(): string {
  return crypto.randomUUID();
}

export function AddFamilyMemberWizard() {
  const { role, user } = useAuth();
  const navigate = useNavigate();
  const memberId = useMemo(() => crypto.randomUUID(), []);
  const [member, setMember] = useState<WizardMember>(() => ({ ...emptyFamilyMember(), id: memberId }));
  const [step, setStep] = useState<StepKey>('basics');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (role !== 'admin') {
    return (
      <div className="mx-auto w-full max-w-5xl space-y-6 px-6 py-6">
        <div className={CARD_CLS}>
          <div className="text-sm text-stone-500">Admins only.</div>
        </div>
      </div>
    );
  }

  function patch(p: Partial<WizardMember>) {
    setMember((m) => ({ ...m, ...p }));
  }

  async function persist(asDraft: boolean): Promise<void> {
    if (!user) throw new Error('No signed-in user.');
    const now = serverTimestamp();
    const payload: Record<string, unknown> = {
      ...member,
      id: memberId,
      status: 'active',
      draft: asDraft,
      revision: (member.revision ?? 0) + 1,
      updatedAt: now,
      updatedBy: user.uid,
    };
    if (!member.createdAt) {
      payload.createdAt = now;
      payload.createdBy = user.uid;
    }
    await setDoc(doc(db, 'familyMembers', memberId), payload, { merge: true });
  }

  async function onSaveDraft() {
    setError(null);
    setSaving(true);
    try {
      await persist(true);
      patch({ draft: true, revision: (member.revision ?? 0) + 1 });
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setSaving(false);
    }
  }

  async function onCreate() {
    setError(null);
    if (!member.legalFirstName.trim() || !member.legalLastName.trim()) {
      setError('Legal first name and legal last name are required.');
      setStep('review');
      return;
    }
    setSaving(true);
    try {
      await persist(false);
      navigate(`/admin/family/${memberId}`);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setSaving(false);
    }
  }

  const stepIdx = STEPS.findIndex((s) => s.key === step);

  return (
    <div className="mx-auto w-full max-w-5xl space-y-6 px-6 py-6">
      <div className="flex items-center justify-between">
        <Link to="/admin/family" className="text-sm text-stone-500 hover:text-stone-700">
          ← Family members
        </Link>
        <div className="text-xs text-stone-500">
          Adding <span className="font-medium text-stone-700">{displayName(member)}</span>
        </div>
      </div>

      <StepIndicator current={step} onJump={setStep} />

      {error && (
        <div className="rounded-2xl border border-[#9C4A3A]/40 bg-[#9C4A3A]/5 p-4 text-sm text-[#9C4A3A]">
          {error}
        </div>
      )}

      {step === 'basics' && <BasicsStep member={member} onChange={patch} />}
      {step === 'contact' && <ContactStep member={member} onChange={patch} />}
      {step === 'identity' && (
        <IdentityStep member={member} memberId={memberId} onChange={patch} uploaderUid={user?.uid ?? ''} />
      )}
      {step === 'medical' && <MedicalStep member={member} memberId={memberId} onChange={patch} />}
      {step === 'financial' && <FinancialStep member={member} onChange={patch} />}
      {step === 'estate' && <EstateStep member={member} memberId={memberId} onChange={patch} />}
      {step === 'review' && <ReviewStep member={member} />}

      <div className={`${CARD_CLS} flex items-center justify-between`}>
        <button
          className={SECONDARY_BTN}
          disabled={stepIdx === 0}
          onClick={() => {
            const prev = STEPS[stepIdx - 1];
            if (prev) setStep(prev.key);
          }}
        >
          Back
        </button>
        <div className="flex items-center gap-2">
          <button className={SECONDARY_BTN} onClick={onSaveDraft} disabled={saving}>
            {saving ? 'Saving…' : 'Save draft'}
          </button>
          {step !== 'review' ? (
            <button
              className={PRIMARY_BTN}
              onClick={() => {
                const next = STEPS[stepIdx + 1];
                if (next) setStep(next.key);
              }}
            >
              Next
            </button>
          ) : (
            <button className={PRIMARY_BTN} onClick={onCreate} disabled={saving}>
              {saving ? 'Creating…' : 'Create family member'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function StepIndicator({ current, onJump }: { current: StepKey; onJump: (s: StepKey) => void }) {
  return (
    <div className={CARD_CLS}>
      <ol className="flex flex-wrap items-center gap-2">
        {STEPS.map((s, i) => {
          const active = s.key === current;
          return (
            <li key={s.key} className="flex items-center gap-2">
              <button
                onClick={() => onJump(s.key)}
                className={
                  'flex items-center gap-2 rounded-full px-3 py-1.5 text-xs font-medium transition ' +
                  (active
                    ? 'bg-[#333F48] text-white'
                    : 'border border-stone-300 bg-white text-stone-600 hover:bg-stone-50')
                }
              >
                <span
                  className={
                    'inline-flex h-5 w-5 items-center justify-center rounded-full text-[10px] ' +
                    (active ? 'bg-white text-[#333F48]' : 'bg-stone-200 text-stone-600')
                  }
                >
                  {i + 1}
                </span>
                {s.label}
              </button>
              {i < STEPS.length - 1 && <span className="text-stone-300">›</span>}
            </li>
          );
        })}
      </ol>
    </div>
  );
}

interface StepProps {
  member: WizardMember;
  onChange: (p: Partial<WizardMember>) => void;
}

function BasicsStep({ member, onChange }: StepProps) {
  return (
    <div className={CARD_CLS}>
      <h2 className="mb-4 text-lg font-semibold text-stone-800">Basics</h2>
      <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
        <Field label="Legal first name *">
          <input
            className={INPUT_CLS}
            value={member.legalFirstName}
            onChange={(e) => onChange({ legalFirstName: e.target.value })}
          />
        </Field>
        <Field label="Legal middle name">
          <input
            className={INPUT_CLS}
            value={member.legalMiddleName ?? ''}
            onChange={(e) => onChange({ legalMiddleName: e.target.value })}
          />
        </Field>
        <Field label="Legal last name *">
          <input
            className={INPUT_CLS}
            value={member.legalLastName}
            onChange={(e) => onChange({ legalLastName: e.target.value })}
          />
        </Field>
        <Field label="Preferred name">
          <input
            className={INPUT_CLS}
            value={member.preferredName ?? ''}
            onChange={(e) => onChange({ preferredName: e.target.value })}
          />
        </Field>
        <Field label="Suffix">
          <input
            className={INPUT_CLS}
            value={member.suffix ?? ''}
            onChange={(e) => onChange({ suffix: e.target.value })}
          />
        </Field>
        <Field label="Date of birth">
          <input
            type="date"
            className={INPUT_CLS}
            value={member.dateOfBirth ?? ''}
            onChange={(e) => onChange({ dateOfBirth: e.target.value })}
          />
        </Field>
        <Field label="Place of birth">
          <input
            className={INPUT_CLS}
            value={member.placeOfBirth ?? ''}
            onChange={(e) => onChange({ placeOfBirth: e.target.value })}
          />
        </Field>
        <Field label="Gender">
          <input
            className={INPUT_CLS}
            value={member.gender ?? ''}
            onChange={(e) => onChange({ gender: e.target.value })}
          />
        </Field>
        <Field label="Pronouns">
          <input
            className={INPUT_CLS}
            value={member.pronouns ?? ''}
            onChange={(e) => onChange({ pronouns: e.target.value })}
          />
        </Field>
        <Field label="Marital status">
          <select
            className={INPUT_CLS}
            value={member.maritalStatus ?? ''}
            onChange={(e) =>
              onChange({ maritalStatus: (e.target.value || undefined) as MaritalStatus | undefined })
            }
          >
            <option value="">—</option>
            {MARITAL_STATUSES.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Relationship to head">
          <select
            className={INPUT_CLS}
            value={member.relationshipToHead ?? ''}
            onChange={(e) =>
              onChange({
                relationshipToHead: (e.target.value || undefined) as FamilyRelationship | undefined,
              })
            }
          >
            <option value="">—</option>
            {FAMILY_RELATIONSHIPS.map((r) => (
              <option key={r} value={r}>
                {r}
              </option>
            ))}
          </select>
        </Field>
      </div>
    </div>
  );
}

function ContactStep({ member, onChange }: StepProps) {
  function setEmails(emails: EmailAddress[]) {
    onChange({ emails });
  }
  function setPhones(phones: PhoneNumber[]) {
    onChange({ phones });
  }
  function setAddresses(addresses: Address[]) {
    onChange({ addresses });
  }
  function setEmergencyContacts(emergencyContacts: EmergencyContact[]) {
    onChange({ emergencyContacts });
  }

  return (
    <div className="space-y-6">
      <div className={CARD_CLS}>
        <SectionHeader
          title="Emails"
          onAdd={() => setEmails([...member.emails, { id: uid(), email: '' }])}
        />
        <div className="space-y-3">
          {member.emails.map((e, i) => (
            <div key={e.id} className="grid grid-cols-1 gap-3 md:grid-cols-[1fr_2fr_auto]">
              <input
                className={INPUT_CLS}
                placeholder="Label"
                value={e.label ?? ''}
                onChange={(ev) => {
                  const next = [...member.emails];
                  next[i] = { ...e, label: ev.target.value };
                  setEmails(next);
                }}
              />
              <input
                className={INPUT_CLS}
                placeholder="email@example.com"
                value={e.email}
                onChange={(ev) => {
                  const next = [...member.emails];
                  next[i] = { ...e, email: ev.target.value };
                  setEmails(next);
                }}
              />
              <button
                className={DANGER_BTN}
                onClick={() => setEmails(member.emails.filter((_, j) => j !== i))}
              >
                Remove
              </button>
            </div>
          ))}
          {member.emails.length === 0 && <div className="text-xs text-stone-400">No emails yet.</div>}
        </div>
      </div>

      <div className={CARD_CLS}>
        <SectionHeader
          title="Phones"
          onAdd={() => setPhones([...member.phones, { id: uid(), kind: 'mobile', number: '' }])}
        />
        <div className="space-y-3">
          {member.phones.map((p, i) => (
            <div key={p.id} className="grid grid-cols-1 gap-3 md:grid-cols-[1fr_2fr_auto]">
              <select
                className={INPUT_CLS}
                value={p.kind}
                onChange={(ev) => {
                  const next = [...member.phones];
                  next[i] = { ...p, kind: ev.target.value as PhoneKind };
                  setPhones(next);
                }}
              >
                {PHONE_KINDS.map((k) => (
                  <option key={k} value={k}>
                    {k}
                  </option>
                ))}
              </select>
              <input
                className={INPUT_CLS}
                placeholder="+1 555 555 5555"
                value={p.number}
                onChange={(ev) => {
                  const next = [...member.phones];
                  next[i] = { ...p, number: ev.target.value };
                  setPhones(next);
                }}
              />
              <button
                className={DANGER_BTN}
                onClick={() => setPhones(member.phones.filter((_, j) => j !== i))}
              >
                Remove
              </button>
            </div>
          ))}
          {member.phones.length === 0 && <div className="text-xs text-stone-400">No phones yet.</div>}
        </div>
      </div>

      <div className={CARD_CLS}>
        <SectionHeader
          title="Addresses"
          onAdd={() => setAddresses([...member.addresses, { id: uid(), kind: 'home' }])}
        />
        <div className="space-y-4">
          {member.addresses.map((a, i) => (
            <div key={a.id} className="rounded-xl border border-stone-200 p-4">
              <div className="mb-3 flex items-center justify-between">
                <select
                  className={INPUT_CLS + ' max-w-[160px]'}
                  value={a.kind}
                  onChange={(ev) => {
                    const next = [...member.addresses];
                    next[i] = { ...a, kind: ev.target.value as AddressKind };
                    setAddresses(next);
                  }}
                >
                  {ADDRESS_KINDS.map((k) => (
                    <option key={k} value={k}>
                      {k}
                    </option>
                  ))}
                </select>
                <button
                  className={DANGER_BTN}
                  onClick={() => setAddresses(member.addresses.filter((_, j) => j !== i))}
                >
                  Remove
                </button>
              </div>
              <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                <Field label="Street line 1">
                  <input
                    className={INPUT_CLS}
                    value={a.line1 ?? ''}
                    onChange={(ev) => {
                      const next = [...member.addresses];
                      next[i] = { ...a, line1: ev.target.value };
                      setAddresses(next);
                    }}
                  />
                </Field>
                <Field label="Street line 2">
                  <input
                    className={INPUT_CLS}
                    value={a.line2 ?? ''}
                    onChange={(ev) => {
                      const next = [...member.addresses];
                      next[i] = { ...a, line2: ev.target.value };
                      setAddresses(next);
                    }}
                  />
                </Field>
                <Field label="City">
                  <input
                    className={INPUT_CLS}
                    value={a.city ?? ''}
                    onChange={(ev) => {
                      const next = [...member.addresses];
                      next[i] = { ...a, city: ev.target.value };
                      setAddresses(next);
                    }}
                  />
                </Field>
                <Field label="State / region">
                  <input
                    className={INPUT_CLS}
                    value={a.state ?? ''}
                    onChange={(ev) => {
                      const next = [...member.addresses];
                      next[i] = { ...a, state: ev.target.value };
                      setAddresses(next);
                    }}
                  />
                </Field>
                <Field label="Postal code">
                  <input
                    className={INPUT_CLS}
                    value={a.postalCode ?? ''}
                    onChange={(ev) => {
                      const next = [...member.addresses];
                      next[i] = { ...a, postalCode: ev.target.value };
                      setAddresses(next);
                    }}
                  />
                </Field>
                <Field label="Country">
                  <input
                    className={INPUT_CLS}
                    value={a.country ?? ''}
                    onChange={(ev) => {
                      const next = [...member.addresses];
                      next[i] = { ...a, country: ev.target.value };
                      setAddresses(next);
                    }}
                  />
                </Field>
              </div>
            </div>
          ))}
          {member.addresses.length === 0 && (
            <div className="text-xs text-stone-400">No addresses yet.</div>
          )}
        </div>
      </div>

      <div className={CARD_CLS}>
        <SectionHeader
          title="Emergency contacts"
          onAdd={() =>
            setEmergencyContacts([...member.emergencyContacts, { id: uid(), name: '' }])
          }
        />
        <div className="space-y-4">
          {member.emergencyContacts.map((c, i) => (
            <div key={c.id} className="rounded-xl border border-stone-200 p-4">
              <div className="mb-3 flex items-center justify-end">
                <button
                  className={DANGER_BTN}
                  onClick={() =>
                    setEmergencyContacts(member.emergencyContacts.filter((_, j) => j !== i))
                  }
                >
                  Remove
                </button>
              </div>
              <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                <Field label="Name">
                  <input
                    className={INPUT_CLS}
                    value={c.name}
                    onChange={(ev) => {
                      const next = [...member.emergencyContacts];
                      next[i] = { ...c, name: ev.target.value };
                      setEmergencyContacts(next);
                    }}
                  />
                </Field>
                <Field label="Relationship">
                  <input
                    className={INPUT_CLS}
                    value={c.relationship ?? ''}
                    onChange={(ev) => {
                      const next = [...member.emergencyContacts];
                      next[i] = { ...c, relationship: ev.target.value };
                      setEmergencyContacts(next);
                    }}
                  />
                </Field>
                <Field label="Phone">
                  <input
                    className={INPUT_CLS}
                    value={c.phone ?? ''}
                    onChange={(ev) => {
                      const next = [...member.emergencyContacts];
                      next[i] = { ...c, phone: ev.target.value };
                      setEmergencyContacts(next);
                    }}
                  />
                </Field>
                <Field label="Email">
                  <input
                    className={INPUT_CLS}
                    value={c.email ?? ''}
                    onChange={(ev) => {
                      const next = [...member.emergencyContacts];
                      next[i] = { ...c, email: ev.target.value };
                      setEmergencyContacts(next);
                    }}
                  />
                </Field>
              </div>
            </div>
          ))}
          {member.emergencyContacts.length === 0 && (
            <div className="text-xs text-stone-400">No emergency contacts yet.</div>
          )}
        </div>
      </div>
    </div>
  );
}

function IdentityStep({
  member,
  memberId,
  onChange,
  uploaderUid,
}: StepProps & { memberId: string; uploaderUid: string }) {
  const [uploading, setUploading] = useState<Record<string, boolean>>({});
  const [uploadError, setUploadError] = useState<string | null>(null);

  function setDocs(docs: IdDocument[]) {
    onChange({ idDocuments: docs });
  }

  async function uploadForRow(rowId: string, file: File) {
    setUploadError(null);
    if (file.size > 25 * 1024 * 1024) {
      setUploadError('Files must be 25MB or smaller.');
      return;
    }
    const row = member.idDocuments.find((d) => d.id === rowId);
    if (!row) return;
    setUploading((u) => ({ ...u, [rowId]: true }));
    try {
      const path = `family/${memberId}/${row.kind}/${uid()}-${sanitizeName(file.name)}`;
      const ref = storageRef(storage, path);
      await uploadBytes(ref, file);
      const url = await getDownloadURL(ref);
      const nextDocs = member.idDocuments.map((d) =>
        d.id === rowId
          ? {
              ...d,
              storagePath: path,
              downloadUrl: url,
              uploadedAt: new Date().toISOString(),
              uploadedBy: uploaderUid,
            }
          : d,
      );
      setDocs(nextDocs);
    } catch (e) {
      setUploadError(e instanceof Error ? e.message : String(e));
    } finally {
      setUploading((u) => ({ ...u, [rowId]: false }));
    }
  }

  return (
    <div className={CARD_CLS}>
      <SectionHeader
        title="Identity documents"
        onAdd={() => setDocs([...member.idDocuments, { id: uid(), kind: 'passport' }])}
      />
      {uploadError && <div className="mb-3 text-sm text-[#9C4A3A]">{uploadError}</div>}
      <div className="space-y-4">
        {member.idDocuments.map((d, i) => (
          <div key={d.id} className="rounded-xl border border-stone-200 p-4">
            <div className="mb-3 flex items-center justify-between">
              <select
                className={INPUT_CLS + ' max-w-[220px]'}
                value={d.kind}
                onChange={(ev) => {
                  const next = [...member.idDocuments];
                  next[i] = { ...d, kind: ev.target.value as IdDocKind };
                  setDocs(next);
                }}
              >
                {ID_DOC_KINDS.map((k) => (
                  <option key={k} value={k}>
                    {k}
                  </option>
                ))}
              </select>
              <button
                className={DANGER_BTN}
                onClick={() => setDocs(member.idDocuments.filter((_, j) => j !== i))}
              >
                Remove
              </button>
            </div>
            <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
              <Field label="Document number">
                <input
                  className={INPUT_CLS}
                  value={d.number ?? ''}
                  onChange={(ev) => {
                    const next = [...member.idDocuments];
                    next[i] = { ...d, number: ev.target.value };
                    setDocs(next);
                  }}
                />
              </Field>
              <Field label="Issuing country">
                <input
                  className={INPUT_CLS}
                  value={d.issuingCountry ?? ''}
                  onChange={(ev) => {
                    const next = [...member.idDocuments];
                    next[i] = { ...d, issuingCountry: ev.target.value };
                    setDocs(next);
                  }}
                />
              </Field>
              <Field label="Issuing state">
                <input
                  className={INPUT_CLS}
                  value={d.issuingState ?? ''}
                  onChange={(ev) => {
                    const next = [...member.idDocuments];
                    next[i] = { ...d, issuingState: ev.target.value };
                    setDocs(next);
                  }}
                />
              </Field>
              <Field label="Issued at">
                <input
                  type="date"
                  className={INPUT_CLS}
                  value={d.issuedAt ?? ''}
                  onChange={(ev) => {
                    const next = [...member.idDocuments];
                    next[i] = { ...d, issuedAt: ev.target.value };
                    setDocs(next);
                  }}
                />
              </Field>
              <Field label="Expires at">
                <input
                  type="date"
                  className={INPUT_CLS}
                  value={d.expiresAt ?? ''}
                  onChange={(ev) => {
                    const next = [...member.idDocuments];
                    next[i] = { ...d, expiresAt: ev.target.value };
                    setDocs(next);
                  }}
                />
              </Field>
              <Field label="File (PDF or image, ≤25MB)">
                <input
                  type="file"
                  accept="application/pdf,image/*"
                  className="block w-full text-sm"
                  onChange={(ev: ChangeEvent<HTMLInputElement>) => {
                    const f = ev.target.files?.[0];
                    if (f) void uploadForRow(d.id, f);
                  }}
                />
                {uploading[d.id] && <div className="mt-1 text-xs text-stone-500">Uploading…</div>}
                {d.downloadUrl && (
                  <a
                    href={d.downloadUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="mt-1 inline-block text-xs text-[#BF5700] hover:underline"
                  >
                    View uploaded file
                  </a>
                )}
              </Field>
            </div>
          </div>
        ))}
        {member.idDocuments.length === 0 && (
          <div className="text-xs text-stone-400">No identity documents yet.</div>
        )}
      </div>
    </div>
  );
}

function MedicalStep({
  member,
  memberId,
  onChange,
}: StepProps & { memberId: string }) {
  const [uploadError, setUploadError] = useState<string | null>(null);
  const med = member.medical;
  const primary: Doctor | undefined = med.doctors.find((d) => d.isPrimary);
  const otherDoctors = med.doctors.filter((d) => !d.isPrimary);

  function setMedical(p: Partial<typeof med>) {
    onChange({ medical: { ...med, ...p } });
  }
  function setDoctors(doctors: Doctor[]) {
    setMedical({ doctors });
  }
  function setPrescriptions(prescriptions: Prescription[]) {
    setMedical({ prescriptions });
  }
  function setInsurance(insurance: InsurancePolicy[]) {
    setMedical({ insurance });
  }

  function updatePrimary(patch: Partial<Doctor>) {
    if (primary) {
      const next = med.doctors.map((d) => (d.id === primary.id ? { ...d, ...patch } : d));
      setDoctors(next);
    } else {
      const newDoc: Doctor = {
        id: uid(),
        name: '',
        specialty: 'Primary Care',
        isPrimary: true,
        ...patch,
      };
      setDoctors([...med.doctors, newDoc]);
      setMedical({ primaryCareDoctorId: newDoc.id });
    }
  }

  async function uploadInsurance(rowId: string, file: File) {
    setUploadError(null);
    if (file.size > 25 * 1024 * 1024) {
      setUploadError('Files must be 25MB or smaller.');
      return;
    }
    try {
      const path = `family/${memberId}/insurance/${uid()}-${sanitizeName(file.name)}`;
      const ref = storageRef(storage, path);
      await uploadBytes(ref, file);
      const url = await getDownloadURL(ref);
      setInsurance(
        med.insurance.map((p) =>
          p.id === rowId ? { ...p, storagePath: path, downloadUrl: url } : p,
        ),
      );
    } catch (e) {
      setUploadError(e instanceof Error ? e.message : String(e));
    }
  }

  return (
    <div className="space-y-6">
      <div className={CARD_CLS}>
        <h2 className="mb-4 text-lg font-semibold text-stone-800">Primary care doctor</h2>
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
          <Field label="Name">
            <input
              className={INPUT_CLS}
              value={primary?.name ?? ''}
              onChange={(e) => updatePrimary({ name: e.target.value })}
            />
          </Field>
          <Field label="Specialty">
            <input
              className={INPUT_CLS}
              value={primary?.specialty ?? 'Primary Care'}
              onChange={(e) => updatePrimary({ specialty: e.target.value })}
            />
          </Field>
          <Field label="Practice">
            <input
              className={INPUT_CLS}
              value={primary?.practice ?? ''}
              onChange={(e) => updatePrimary({ practice: e.target.value })}
            />
          </Field>
          <Field label="Phone">
            <input
              className={INPUT_CLS}
              value={primary?.phone ?? ''}
              onChange={(e) => updatePrimary({ phone: e.target.value })}
            />
          </Field>
        </div>
      </div>

      <div className={CARD_CLS}>
        <SectionHeader
          title="Other doctors"
          onAdd={() => setDoctors([...med.doctors, { id: uid(), name: '' }])}
        />
        <div className="space-y-4">
          {otherDoctors.map((d) => {
            const idx = med.doctors.findIndex((x) => x.id === d.id);
            return (
              <div key={d.id} className="rounded-xl border border-stone-200 p-4">
                <div className="mb-3 flex items-center justify-end">
                  <button
                    className={DANGER_BTN}
                    onClick={() => setDoctors(med.doctors.filter((x) => x.id !== d.id))}
                  >
                    Remove
                  </button>
                </div>
                <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                  <Field label="Name">
                    <input
                      className={INPUT_CLS}
                      value={d.name}
                      onChange={(e) => {
                        const next = [...med.doctors];
                        next[idx] = { ...d, name: e.target.value };
                        setDoctors(next);
                      }}
                    />
                  </Field>
                  <Field label="Specialty">
                    <input
                      className={INPUT_CLS}
                      value={d.specialty ?? ''}
                      onChange={(e) => {
                        const next = [...med.doctors];
                        next[idx] = { ...d, specialty: e.target.value };
                        setDoctors(next);
                      }}
                    />
                  </Field>
                  <Field label="Practice">
                    <input
                      className={INPUT_CLS}
                      value={d.practice ?? ''}
                      onChange={(e) => {
                        const next = [...med.doctors];
                        next[idx] = { ...d, practice: e.target.value };
                        setDoctors(next);
                      }}
                    />
                  </Field>
                  <Field label="Phone">
                    <input
                      className={INPUT_CLS}
                      value={d.phone ?? ''}
                      onChange={(e) => {
                        const next = [...med.doctors];
                        next[idx] = { ...d, phone: e.target.value };
                        setDoctors(next);
                      }}
                    />
                  </Field>
                </div>
              </div>
            );
          })}
          {otherDoctors.length === 0 && (
            <div className="text-xs text-stone-400">No additional doctors yet.</div>
          )}
        </div>
      </div>

      <div className={CARD_CLS}>
        <SectionHeader
          title="Prescriptions"
          onAdd={() => setPrescriptions([...med.prescriptions, { id: uid(), drug: '' }])}
        />
        <div className="space-y-4">
          {med.prescriptions.map((p, i) => (
            <div key={p.id} className="rounded-xl border border-stone-200 p-4">
              <div className="mb-3 flex items-center justify-end">
                <button
                  className={DANGER_BTN}
                  onClick={() => setPrescriptions(med.prescriptions.filter((_, j) => j !== i))}
                >
                  Remove
                </button>
              </div>
              <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                <Field label="Drug">
                  <input
                    className={INPUT_CLS}
                    value={p.drug}
                    onChange={(e) => {
                      const next = [...med.prescriptions];
                      next[i] = { ...p, drug: e.target.value };
                      setPrescriptions(next);
                    }}
                  />
                </Field>
                <Field label="Dose">
                  <input
                    className={INPUT_CLS}
                    value={p.dose ?? ''}
                    onChange={(e) => {
                      const next = [...med.prescriptions];
                      next[i] = { ...p, dose: e.target.value };
                      setPrescriptions(next);
                    }}
                  />
                </Field>
                <Field label="Frequency">
                  <input
                    className={INPUT_CLS}
                    value={p.frequency ?? ''}
                    onChange={(e) => {
                      const next = [...med.prescriptions];
                      next[i] = { ...p, frequency: e.target.value };
                      setPrescriptions(next);
                    }}
                  />
                </Field>
                <Field label="Prescriber">
                  <select
                    className={INPUT_CLS}
                    value={p.prescriberDoctorId ?? ''}
                    onChange={(e) => {
                      const next = [...med.prescriptions];
                      next[i] = { ...p, prescriberDoctorId: e.target.value || undefined };
                      setPrescriptions(next);
                    }}
                  >
                    <option value="">—</option>
                    {med.doctors.map((d) => (
                      <option key={d.id} value={d.id}>
                        {d.name || '(unnamed)'} {d.specialty ? `– ${d.specialty}` : ''}
                      </option>
                    ))}
                  </select>
                </Field>
                <Field label="Pharmacy">
                  <input
                    className={INPUT_CLS}
                    value={p.pharmacy ?? ''}
                    onChange={(e) => {
                      const next = [...med.prescriptions];
                      next[i] = { ...p, pharmacy: e.target.value };
                      setPrescriptions(next);
                    }}
                  />
                </Field>
                <Field label="Pharmacy phone">
                  <input
                    className={INPUT_CLS}
                    value={p.pharmacyPhone ?? ''}
                    onChange={(e) => {
                      const next = [...med.prescriptions];
                      next[i] = { ...p, pharmacyPhone: e.target.value };
                      setPrescriptions(next);
                    }}
                  />
                </Field>
                <Field label="Rx number">
                  <input
                    className={INPUT_CLS}
                    value={p.rxNumber ?? ''}
                    onChange={(e) => {
                      const next = [...med.prescriptions];
                      next[i] = { ...p, rxNumber: e.target.value };
                      setPrescriptions(next);
                    }}
                  />
                </Field>
              </div>
            </div>
          ))}
          {med.prescriptions.length === 0 && (
            <div className="text-xs text-stone-400">No prescriptions yet.</div>
          )}
        </div>
      </div>

      <div className={CARD_CLS}>
        <h2 className="mb-4 text-lg font-semibold text-stone-800">Health summary</h2>
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <Field label="Allergies (comma-separated)">
            <ChipInput
              values={med.allergies ?? []}
              onChange={(allergies) => setMedical({ allergies })}
              placeholder="penicillin, peanuts"
            />
          </Field>
          <Field label="Chronic conditions (comma-separated)">
            <ChipInput
              values={med.chronicConditions ?? []}
              onChange={(chronicConditions) => setMedical({ chronicConditions })}
              placeholder="asthma, type 2 diabetes"
            />
          </Field>
          <Field label="Blood type">
            <select
              className={INPUT_CLS}
              value={med.bloodType ?? ''}
              onChange={(e) =>
                setMedical({ bloodType: (e.target.value || undefined) as BloodType | undefined })
              }
            >
              <option value="">—</option>
              {BLOOD_TYPES.map((b) => (
                <option key={b} value={b}>
                  {b}
                </option>
              ))}
            </select>
          </Field>
        </div>
      </div>

      <div className={CARD_CLS}>
        <SectionHeader
          title="Insurance policies"
          onAdd={() =>
            setInsurance([...med.insurance, { id: uid(), kind: 'health', carrier: '' }])
          }
        />
        {uploadError && <div className="mb-3 text-sm text-[#9C4A3A]">{uploadError}</div>}
        <div className="space-y-4">
          {med.insurance.map((p, i) => (
            <div key={p.id} className="rounded-xl border border-stone-200 p-4">
              <div className="mb-3 flex items-center justify-end">
                <button
                  className={DANGER_BTN}
                  onClick={() => setInsurance(med.insurance.filter((_, j) => j !== i))}
                >
                  Remove
                </button>
              </div>
              <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                <Field label="Kind">
                  <select
                    className={INPUT_CLS}
                    value={p.kind}
                    onChange={(e) => {
                      const next = [...med.insurance];
                      next[i] = { ...p, kind: e.target.value as InsurancePolicy['kind'] };
                      setInsurance(next);
                    }}
                  >
                    {INSURANCE_KINDS.map((k) => (
                      <option key={k} value={k}>
                        {k}
                      </option>
                    ))}
                  </select>
                </Field>
                <Field label="Carrier">
                  <input
                    className={INPUT_CLS}
                    value={p.carrier}
                    onChange={(e) => {
                      const next = [...med.insurance];
                      next[i] = { ...p, carrier: e.target.value };
                      setInsurance(next);
                    }}
                  />
                </Field>
                <Field label="Policy number">
                  <input
                    className={INPUT_CLS}
                    value={p.policyNumber ?? ''}
                    onChange={(e) => {
                      const next = [...med.insurance];
                      next[i] = { ...p, policyNumber: e.target.value };
                      setInsurance(next);
                    }}
                  />
                </Field>
                <Field label="Group number">
                  <input
                    className={INPUT_CLS}
                    value={p.groupNumber ?? ''}
                    onChange={(e) => {
                      const next = [...med.insurance];
                      next[i] = { ...p, groupNumber: e.target.value };
                      setInsurance(next);
                    }}
                  />
                </Field>
                <Field label="Member ID">
                  <input
                    className={INPUT_CLS}
                    value={p.memberId ?? ''}
                    onChange={(e) => {
                      const next = [...med.insurance];
                      next[i] = { ...p, memberId: e.target.value };
                      setInsurance(next);
                    }}
                  />
                </Field>
                <Field label="Phone">
                  <input
                    className={INPUT_CLS}
                    value={p.phone ?? ''}
                    onChange={(e) => {
                      const next = [...med.insurance];
                      next[i] = { ...p, phone: e.target.value };
                      setInsurance(next);
                    }}
                  />
                </Field>
                <Field label="Effective at">
                  <input
                    type="date"
                    className={INPUT_CLS}
                    value={p.effectiveAt ?? ''}
                    onChange={(e) => {
                      const next = [...med.insurance];
                      next[i] = { ...p, effectiveAt: e.target.value };
                      setInsurance(next);
                    }}
                  />
                </Field>
                <Field label="Expires at">
                  <input
                    type="date"
                    className={INPUT_CLS}
                    value={p.expiresAt ?? ''}
                    onChange={(e) => {
                      const next = [...med.insurance];
                      next[i] = { ...p, expiresAt: e.target.value };
                      setInsurance(next);
                    }}
                  />
                </Field>
                <Field label="Insurance card (optional)">
                  <input
                    type="file"
                    accept="application/pdf,image/*"
                    className="block w-full text-sm"
                    onChange={(ev) => {
                      const f = ev.target.files?.[0];
                      if (f) void uploadInsurance(p.id, f);
                    }}
                  />
                  {p.downloadUrl && (
                    <a
                      href={p.downloadUrl}
                      target="_blank"
                      rel="noreferrer"
                      className="mt-1 inline-block text-xs text-[#BF5700] hover:underline"
                    >
                      View uploaded file
                    </a>
                  )}
                </Field>
              </div>
            </div>
          ))}
          {med.insurance.length === 0 && (
            <div className="text-xs text-stone-400">No insurance policies yet.</div>
          )}
        </div>
      </div>
    </div>
  );
}

function FinancialStep({ member, onChange }: StepProps) {
  const fin = member.financial;
  function setFinancial(p: Partial<typeof fin>) {
    onChange({ financial: { ...fin, ...p } });
  }
  return (
    <div className="space-y-6">
      <div className={CARD_CLS}>
        <h2 className="mb-4 text-lg font-semibold text-stone-800">Financial</h2>
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <Field label="Occupation">
            <input
              className={INPUT_CLS}
              value={fin.occupation ?? ''}
              onChange={(e) => setFinancial({ occupation: e.target.value })}
            />
          </Field>
          <Field label="Employer">
            <input
              className={INPUT_CLS}
              value={fin.employer ?? ''}
              onChange={(e) => setFinancial({ employer: e.target.value })}
            />
          </Field>
          <Field label="SSN last 4">
            <input
              className={INPUT_CLS}
              inputMode="numeric"
              maxLength={4}
              pattern="\d{4}"
              value={fin.ssnLast4 ?? ''}
              onChange={(e) => {
                const digits = e.target.value.replace(/\D/g, '').slice(0, 4);
                setFinancial({ ssnLast4: digits });
              }}
              placeholder="••••"
            />
          </Field>
          <Field label="Tax residency country">
            <input
              className={INPUT_CLS}
              value={fin.taxResidencyCountry ?? ''}
              onChange={(e) => setFinancial({ taxResidencyCountry: e.target.value })}
            />
          </Field>
          <Field label="Tax residency state">
            <input
              className={INPUT_CLS}
              value={fin.taxResidencyState ?? ''}
              onChange={(e) => setFinancial({ taxResidencyState: e.target.value })}
            />
          </Field>
          <Field label="Citizenships">
            <ChipInput
              values={fin.citizenships ?? []}
              onChange={(citizenships) => setFinancial({ citizenships })}
              placeholder="US, IL"
            />
          </Field>
          <Field label="Net worth band">
            <select
              className={INPUT_CLS}
              value={fin.netWorthBand ?? ''}
              onChange={(e) =>
                setFinancial({
                  netWorthBand: (e.target.value || undefined) as
                    | NonNullable<FamilyMember['financial']['netWorthBand']>
                    | undefined,
                })
              }
            >
              <option value="">—</option>
              {NET_WORTH_BANDS.map((b) => (
                <option key={b.value} value={b.value}>
                  {b.label}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Notes">
            <textarea
              className={INPUT_CLS + ' min-h-[80px]'}
              value={fin.notes ?? ''}
              onChange={(e) => setFinancial({ notes: e.target.value })}
            />
          </Field>
        </div>
      </div>

      <div className={CARD_CLS}>
        <div className={LABEL_CLS + ' mb-2'}>Linked accounts</div>
        <div className="rounded-xl border border-dashed border-stone-300 bg-stone-50 p-4 text-sm text-stone-500">
          <div className="mb-2 font-medium text-stone-700">Link Plaid account</div>
          <button className={SECONDARY_BTN} disabled>
            Coming soon — connect at /integrations/plaid
          </button>
        </div>
      </div>
    </div>
  );
}

function EstateStep({
  member,
  memberId,
  onChange,
}: StepProps & { memberId: string }) {
  const est = member.estate;
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [willUrl, setWillUrl] = useState<string | undefined>(undefined);
  const [livingWillUrl, setLivingWillUrl] = useState<string | undefined>(undefined);

  function setEstate(p: Partial<typeof est>) {
    onChange({ estate: { ...est, ...p } });
  }

  async function uploadEstate(kind: 'will' | 'living-will', file: File) {
    setUploadError(null);
    if (file.size > 25 * 1024 * 1024) {
      setUploadError('Files must be 25MB or smaller.');
      return;
    }
    try {
      const path = `family/${memberId}/estate/${kind}/${uid()}-${sanitizeName(file.name)}`;
      const ref = storageRef(storage, path);
      await uploadBytes(ref, file);
      const url = await getDownloadURL(ref);
      if (kind === 'will') {
        setEstate({ willStoragePath: path });
        setWillUrl(url);
      } else {
        setEstate({ livingWillStoragePath: path });
        setLivingWillUrl(url);
      }
    } catch (e) {
      setUploadError(e instanceof Error ? e.message : String(e));
    }
  }

  return (
    <div className={CARD_CLS}>
      <h2 className="mb-4 text-lg font-semibold text-stone-800">Estate</h2>
      {uploadError && <div className="mb-3 text-sm text-[#9C4A3A]">{uploadError}</div>}
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <Field label="Has will?">
          <label className="inline-flex items-center gap-2 text-sm text-stone-700">
            <input
              type="checkbox"
              checked={!!est.hasWill}
              onChange={(e) => setEstate({ hasWill: e.target.checked })}
            />
            Yes
          </label>
        </Field>
        <Field label="Has trust?">
          <label className="inline-flex items-center gap-2 text-sm text-stone-700">
            <input
              type="checkbox"
              checked={!!est.hasTrust}
              onChange={(e) => setEstate({ hasTrust: e.target.checked })}
            />
            Yes
          </label>
        </Field>
        <Field label="Trust names">
          <ChipInput
            values={est.trustNames ?? []}
            onChange={(trustNames) => setEstate({ trustNames })}
            placeholder="Family Trust 2024"
          />
        </Field>
        <Field label="Executor">
          <input
            className={INPUT_CLS}
            value={est.executor ?? ''}
            onChange={(e) => setEstate({ executor: e.target.value })}
          />
        </Field>
        <Field label="Power of attorney">
          <input
            className={INPUT_CLS}
            value={est.powerOfAttorney ?? ''}
            onChange={(e) => setEstate({ powerOfAttorney: e.target.value })}
          />
        </Field>
        <Field label="Healthcare proxy">
          <input
            className={INPUT_CLS}
            value={est.healthcareProxy ?? ''}
            onChange={(e) => setEstate({ healthcareProxy: e.target.value })}
          />
        </Field>
        <Field label="Beneficiary notes">
          <textarea
            className={INPUT_CLS + ' min-h-[80px]'}
            value={est.beneficiaryNotes ?? ''}
            onChange={(e) => setEstate({ beneficiaryNotes: e.target.value })}
          />
        </Field>
        <Field label="Will file">
          <input
            type="file"
            accept="application/pdf,image/*"
            className="block w-full text-sm"
            onChange={(ev) => {
              const f = ev.target.files?.[0];
              if (f) void uploadEstate('will', f);
            }}
          />
          {(willUrl || est.willStoragePath) && (
            <div className="mt-1 text-xs text-stone-500">
              {willUrl ? (
                <a
                  href={willUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="text-[#BF5700] hover:underline"
                >
                  View uploaded will
                </a>
              ) : (
                <span>Stored at {est.willStoragePath}</span>
              )}
            </div>
          )}
        </Field>
        <Field label="Living will file">
          <input
            type="file"
            accept="application/pdf,image/*"
            className="block w-full text-sm"
            onChange={(ev) => {
              const f = ev.target.files?.[0];
              if (f) void uploadEstate('living-will', f);
            }}
          />
          {(livingWillUrl || est.livingWillStoragePath) && (
            <div className="mt-1 text-xs text-stone-500">
              {livingWillUrl ? (
                <a
                  href={livingWillUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="text-[#BF5700] hover:underline"
                >
                  View uploaded living will
                </a>
              ) : (
                <span>Stored at {est.livingWillStoragePath}</span>
              )}
            </div>
          )}
        </Field>
      </div>
    </div>
  );
}

function ReviewStep({ member }: { member: WizardMember }) {
  const missing: string[] = [];
  if (!member.legalFirstName.trim()) missing.push('Legal first name');
  if (!member.legalLastName.trim()) missing.push('Legal last name');

  return (
    <div className="space-y-6">
      <div className={CARD_CLS}>
        <h2 className="mb-4 text-lg font-semibold text-stone-800">Review</h2>
        {missing.length > 0 && (
          <div className="mb-4 rounded border border-[#9C4A3A]/40 bg-[#9C4A3A]/5 p-3 text-sm text-[#9C4A3A]">
            Missing required fields: {missing.join(', ')}
          </div>
        )}
        <ReviewRow label="Display name" value={displayName(member)} />
        <ReviewRow
          label="Legal name"
          value={[member.legalFirstName, member.legalMiddleName, member.legalLastName, member.suffix]
            .filter(Boolean)
            .join(' ')}
        />
        <ReviewRow label="Preferred name" value={member.preferredName} />
        <ReviewRow label="Date of birth" value={member.dateOfBirth} />
        <ReviewRow label="Place of birth" value={member.placeOfBirth} />
        <ReviewRow label="Gender" value={member.gender} />
        <ReviewRow label="Pronouns" value={member.pronouns} />
        <ReviewRow label="Marital status" value={member.maritalStatus} />
        <ReviewRow label="Relationship to head" value={member.relationshipToHead} />
      </div>

      <div className={CARD_CLS}>
        <h3 className="mb-3 text-sm font-semibold text-stone-700">Contact</h3>
        <ReviewRow label="Emails" value={member.emails.map((e) => e.email).join(', ')} />
        <ReviewRow
          label="Phones"
          value={member.phones.map((p) => `${p.kind}: ${p.number}`).join(', ')}
        />
        <ReviewRow
          label="Addresses"
          value={member.addresses
            .map((a) => `${a.kind}: ${[a.line1, a.city, a.state, a.country].filter(Boolean).join(', ')}`)
            .join(' | ')}
        />
        <ReviewRow
          label="Emergency contacts"
          value={member.emergencyContacts.map((c) => c.name).join(', ')}
        />
      </div>

      <div className={CARD_CLS}>
        <h3 className="mb-3 text-sm font-semibold text-stone-700">Identity documents</h3>
        <ReviewRow
          label="Documents"
          value={member.idDocuments
            .map((d) => `${d.kind}${d.number ? ` #${d.number}` : ''}`)
            .join(', ')}
        />
      </div>

      <div className={CARD_CLS}>
        <h3 className="mb-3 text-sm font-semibold text-stone-700">Medical</h3>
        <ReviewRow
          label="Doctors"
          value={member.medical.doctors.map((d) => `${d.name}${d.isPrimary ? ' (PCP)' : ''}`).join(', ')}
        />
        <ReviewRow
          label="Prescriptions"
          value={member.medical.prescriptions.map((p) => p.drug).join(', ')}
        />
        <ReviewRow label="Allergies" value={(member.medical.allergies ?? []).join(', ')} />
        <ReviewRow
          label="Chronic conditions"
          value={(member.medical.chronicConditions ?? []).join(', ')}
        />
        <ReviewRow label="Blood type" value={member.medical.bloodType} />
        <ReviewRow
          label="Insurance"
          value={member.medical.insurance.map((p: InsurancePolicy) => `${p.kind}/${p.carrier}`).join(', ')}
        />
      </div>

      <div className={CARD_CLS}>
        <h3 className="mb-3 text-sm font-semibold text-stone-700">Financial</h3>
        <ReviewRow label="Occupation" value={member.financial.occupation} />
        <ReviewRow label="Employer" value={member.financial.employer} />
        <ReviewRow
          label="SSN last 4"
          value={member.financial.ssnLast4 ? `••• ${member.financial.ssnLast4}` : undefined}
        />
        <ReviewRow label="Tax residency country" value={member.financial.taxResidencyCountry} />
        <ReviewRow label="Tax residency state" value={member.financial.taxResidencyState} />
        <ReviewRow label="Citizenships" value={(member.financial.citizenships ?? []).join(', ')} />
        <ReviewRow label="Net worth band" value={member.financial.netWorthBand} />
      </div>

      <div className={CARD_CLS}>
        <h3 className="mb-3 text-sm font-semibold text-stone-700">Estate</h3>
        <ReviewRow label="Has will" value={member.estate.hasWill ? 'Yes' : 'No'} />
        <ReviewRow label="Has trust" value={member.estate.hasTrust ? 'Yes' : 'No'} />
        <ReviewRow label="Trust names" value={(member.estate.trustNames ?? []).join(', ')} />
        <ReviewRow label="Executor" value={member.estate.executor} />
        <ReviewRow label="Power of attorney" value={member.estate.powerOfAttorney} />
        <ReviewRow label="Healthcare proxy" value={member.estate.healthcareProxy} />
        <ReviewRow label="Will file" value={member.estate.willStoragePath} />
        <ReviewRow label="Living will file" value={member.estate.livingWillStoragePath} />
      </div>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block space-y-1">
      <div className={LABEL_CLS}>{label}</div>
      {children}
    </label>
  );
}

function SectionHeader({ title, onAdd }: { title: string; onAdd: () => void }) {
  return (
    <div className="mb-4 flex items-center justify-between">
      <h2 className="text-lg font-semibold text-stone-800">{title}</h2>
      <button className={SECONDARY_BTN} onClick={onAdd}>
        + Add
      </button>
    </div>
  );
}

function ReviewRow({ label, value }: { label: string; value: string | undefined }) {
  return (
    <div className="flex items-start gap-4 border-b border-stone-100 py-2 last:border-b-0">
      <div className={LABEL_CLS + ' w-40 shrink-0 pt-1'}>{label}</div>
      <div className="flex-1 text-sm text-stone-700">
        {value && value.trim() ? value : <span className="text-stone-400">—</span>}
      </div>
    </div>
  );
}

function ChipInput({
  values,
  onChange,
  placeholder,
}: {
  values: string[];
  onChange: (next: string[]) => void;
  placeholder?: string;
}) {
  const [text, setText] = useState('');
  function commit(raw: string) {
    const parts = raw
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean);
    if (parts.length === 0) return;
    const next = Array.from(new Set([...values, ...parts]));
    onChange(next);
    setText('');
  }
  return (
    <div>
      <div className="mb-2 flex flex-wrap gap-2">
        {values.map((v) => (
          <span
            key={v}
            className="inline-flex items-center gap-1 rounded-full bg-stone-100 px-2 py-0.5 text-xs text-stone-700"
          >
            {v}
            <button
              className="text-stone-400 hover:text-[#9C4A3A]"
              onClick={() => onChange(values.filter((x) => x !== v))}
            >
              ×
            </button>
          </span>
        ))}
      </div>
      <input
        className={INPUT_CLS}
        placeholder={placeholder}
        value={text}
        onChange={(e) => setText(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ',') {
            e.preventDefault();
            commit(text);
          }
        }}
        onBlur={() => commit(text)}
      />
    </div>
  );
}
